/**
 * Sysadmin Settings — parent layout (Wave 3 §10.2 #5 / spec §3.8).
 */

import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { Palette, KeyRound, Bell, Webhook, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/settings')({
  component: SettingsLayout,
});

// Foundations 1 / B0-T1: "Feature Toggles" and "Tier Templates" are gone with
// the flags and the plans (decisions.md 2026-09-08). Branding is the first tab
// now; the two tenant settings that survived belong to the TENANT admin, on
// Admin › Settings › General, not here.
const SUB_TABS: Array<{
  key: string; label: string; icon: typeof Palette; to: string;
  status: 'v1' | 'v2' | 'future';
  external?: boolean;
}> = [
  { key: 'branding',      label: 'Branding',        icon: Palette,    to: '/sysadmin/branding',               status: 'v1', external: true },
  { key: 'idp',           label: 'IdP Defaults',    icon: KeyRound,   to: '/sysadmin/settings/idp',           status: 'v1' },
  { key: 'notifications', label: 'Notifications',   icon: Bell,       to: '/sysadmin/settings/notifications', status: 'v1' },
  { key: 'email',         label: 'Email (SMTP)',    icon: Mail,       to: '/sysadmin/settings/email',         status: 'v1' },
  { key: 'api',           label: 'API Access',      icon: Webhook,    to: '/sysadmin/settings/api',           status: 'v1' },
];

function SettingsLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b sticky top-0 bg-background z-10">
        <nav className="flex items-center gap-0.5 overflow-x-auto px-6">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active = !t.external && (location.pathname === t.to ||
              location.pathname.startsWith(t.to + '/'));
            return (
              <Link
                key={t.key}
                to={t.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.status !== 'v1' && (
                  <Badge variant="secondary" className="ml-1 text-[10px]">
                    {t.status}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
